import type {ReactNode} from 'react'
import {useState} from 'react'
import {useNavigate} from 'react-router'
import sortBy from 'lodash/sortBy'
import type {NonEmptyArray} from '@freckle/non-empty'
import {useSafeCallback, useSafeCallbackExtraDeps} from '@freckle/react-hooks'
import {unCallbackFn} from '@freckle/react-hooks'
import {DelayedSpinner} from '@freckle/educator-entities/ts/common/components/spinner-wrapper/delayed-spinner'
import {HttpError} from '@freckle/educator-entities/ts/common/exceptions/http-error'
import type {OperationLocationT} from '@freckle/educator-entities/ts/common/helpers/ajax'
import {equalExtra, staticExtra} from '@freckle/educator-entities/ts/common/helpers/extra-deps'
import {mthens} from '@freckle/educator-entities/ts/common/helpers/maybe'
import {useOperationPersistence} from '@freckle/educator-entities/ts/common/hooks/operations/use-operation-persistence'
import {partitionResults} from '@freckle/educator-entities/ts/common/logic/roster-sync'
import type {ArtifactData} from '@freckle/educator-entities/ts/common/models/artifact'
import {
  parseErrorResponse,
  getAffirmedErrors,
  getUnaffirmedErrors,
} from '@freckle/educator-entities/ts/common/models/csv-validation-errors'
import {spanDangerous} from '@freckle/educator-entities/ts/common/models/html-text'
import type {SyncResultT} from '@freckle/educator-entities/ts/common/models/roster-sync'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import {getActiveIdps} from '@freckle/educator-entities/ts/roster/models/school'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {Container} from '@freckle/educator-materials/ts/components/container'
import type {TranslateT} from '@freckle/educator-materials/ts/helpers/translate'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {PageWrapper} from '@freckle/school/ts/common/components/common/page-wrapper'
import {PendingOperationWhenLocation} from '@freckle/school/ts/common/components/common/pending-operation'
import RosterApi from '@freckle/school/ts/common/helpers/api/rostering'
import {updateSchool} from '@freckle/school/ts/common/helpers/api/schools'
import {
  logError,
  logErrorAsUnhandled,
} from '@freckle/school/ts/common/helpers/exception-handlers/bugsnag-client'
import Notification from '@freckle/school/ts/common/helpers/notification'
import type {SummaryT} from '@freckle/school/ts/common/models/summary'
import {Routes} from '@freckle/school/ts/common/routers/routes'
import {calculateUnmanagedStudents} from '@freckle/school/ts/rostering/components/clean-up-roster/clean-up-phase'
import ConfirmMergeModal from '@freckle/school/ts/rostering/components/common/confirm-merge-modal'
import ExitModal from '@freckle/school/ts/rostering/components/common/exit-modal'
import {FinishedMerging} from '@freckle/school/ts/rostering/components/common/finished-merging'
import RosterBody from '@freckle/school/ts/rostering/components/common/roster-body'
import {CleanUpRoster} from '@freckle/school/ts/rostering/components/import-roster/clean-up-roster'
import ConfirmRosterSync from '@freckle/school/ts/rostering/components/import-roster/confirm-roster-sync'
import FailedImport from '@freckle/school/ts/rostering/components/import-roster/failed-import'
import type {FailedImportErrorT} from '@freckle/school/ts/rostering/components/import-roster/failed-import'
import GetStarted from '@freckle/school/ts/rostering/components/import-roster/get-started'
import ImportRosterFooter from '@freckle/school/ts/rostering/components/import-roster/import-roster-footer'
import type {SchoolAdminAttrs} from '@freckle/school/ts/users/models/school-admin'
import {AttachRosters} from '../attach-rosters'
import {MatchSisId} from '../match-sisid'
import {
  ensureSomeErrorIsShown,
  onlyUnexpectedError,
  failedImportErrors,
  failedImportErrorsUnaffirmed,
  failedItemsFromSync,
  partitionAffirmedErrors,
  uniqueSchoolIdsFromErrors,
  errorsFromArtifacts,
} from './errors'
import type {RosterFileTypeT} from './utils'
import {pageWrapper} from './import-roster-container.module.scss'

// No more than 10MB per file
const MAX_ATTACHMENT_FILE_SIZE = 10 * 1024 * 1024

type ErrorStatusT = 'file-too-large' | 'no-error'

type ImportPhaseT =
  | {
      tag: 'attachment'
    }
  | {
      tag: 'sis-id-matching'
      value: Array<string>
    }
  | {
      tag: 'validation-errors'
      value: NonEmptyArray<FailedImportErrorT>
    }
  | {
      tag: 'confirmation'
      value: Array<SyncResultT>
    }

export type RosterFileT = {
  tag: RosterFileTypeT
  file: File
  errorStatus: ErrorStatusT
}

export type AllRosterFiles = {
  courses: File
  teachers: File
  students: File
  enrollments: File
}

type Props = {
  schoolAdmin: SchoolAdminAttrs
  schools: Array<SchoolAttrs>
  initialSummary: SummaryT
}

export type SelectedMatchT = {
  sisId: string
  school: SchoolAttrs
}

type ModalT = 'early-exit' | 'confirm-dedup-merge'

/**
 * Order of operations:
 * 1) File attachments
 * 2) Merge duplicates if merge checkbox is checked
 *    a) Unknown SISID Errors - Show SISID matching
 *    b) CSV Errors - Show Failed Imports Errors
 *    C) No Errors - Go to #3
 * 3) Roster Sync dry run
 *    a) Unknown SISID Errors - Show SISID matching
 *    b) CSV Errors/Sync Failures - Show Failed Imports Errors
 *    C) No Errors - Show Confirmation page
 * 4) Roster Sync - Real
 */

const PERSISTENCE_KEY = {
  MERGE_DUPLICATES: 'merge-duplicate-students',
  UPLOAD_ROSTER: 'upload-roster',
}

export function ImportRosterContainer(props: Props) {
  const {t} = useSafeTranslation()
  const navigate = useNavigate()
  const {schoolAdmin, schools, initialSummary} = props

  const operationPersistence = useOperationPersistence()

  const [files, setFiles] = useState<Array<RosterFileT>>([])
  const [loadingText, setLoadingText] = useState<string | null>(null)
  const [sisIdMatchingAttempted, setSisIdMatchingAttempted] = useState(false)
  const [modal, setModal] = useState<ModalT | null>(null)
  const [phase, setPhase] = useState<ImportPhaseT>({tag: 'attachment'})
  const [isDedupMergeChecked, setIsDedupMergeChecked] = useState(false)
  const [unrecognizedSisIdsAddressed, setUnrecognizedSisIdsAddressed] = useState(false)
  const [matchedSelections, setMatchedSelections] = useState<Array<SelectedMatchT>>([])
  const [mergeDuplicatesOperationLocation, setMergeDuplicatesOperationLocation] =
    useState<OperationLocationT | null>(
      operationPersistence.pendingLocationFor(PERSISTENCE_KEY.MERGE_DUPLICATES),
    )
  const [uploadRosterOperationLocation, setUploadRosterOperationLocation] =
    useState<OperationLocationT | null>(
      operationPersistence.pendingLocationFor(PERSISTENCE_KEY.UPLOAD_ROSTER),
    )

  const mAllRosterFiles = parseAllValidatedRosterFiles(files)

  const mergeStudentsError = t('MERGE_STUDENTS_ERROR')

  const onMergeDuplicateStudentsFinish = useSafeCallbackExtraDeps(
    ({persistence}: {persistence: typeof operationPersistence}) =>
      () =>
        persistence.remove(PERSISTENCE_KEY.MERGE_DUPLICATES),
    [],
    {
      persistence: {value: operationPersistence, comparator: () => true},
    },
  )

  const onMergeDuplicateStudentsFailure = useSafeCallback(
    () => () => {
      setMergeDuplicatesOperationLocation(null)
      Notification.displayErrorNotification(mergeStudentsError)
      setPhase({tag: 'attachment'})
      setLoadingText(null)
    },
    [mergeStudentsError],
  )

  const validatedRosterText = t('IMPORT_ROSTER_VALIDATING')

  // Job should not "fail" as far as operation results are concerned. If it
  // fails in some way, it should write the failure to an artifact that would be
  // passed to the onSuccess handler.
  const onUploadRosterFailure = useSafeCallbackExtraDeps<() => void, {t: typeof t}>(
    ({t}) =>
      () => {
        setUploadRosterOperationLocation(null)
        logError(new HttpError(new Error('Totally unexpected error occurred while syncing roster')))
        setPhase({
          tag: 'validation-errors',
          value: onlyUnexpectedError(t),
        })
      },
    [],
    {
      t: staticExtra(t),
    },
  )

  const updateSuccessMessage = t('IMPORT_ROSTER_SUCCESS_MESSAGE')

  const onUploadRosterSuccess = useSafeCallbackExtraDeps<
    (artifacts: ArtifactData[]) => void,
    {navigate: typeof navigate; t: typeof t}
  >(
    ({navigate, t}) =>
      (artifacts: ArtifactData[]) => {
        setUploadRosterOperationLocation(null)

        const mErrors = errorsFromArtifacts(
          artifacts.map(a => a.data),
          t,
        )

        if (mErrors === null) {
          Notification.displaySuccessNotification(updateSuccessMessage)
          navigate(Routes.dashboard())
        } else {
          setPhase({
            tag: 'validation-errors',
            value: mErrors,
          })
        }
      },
    [updateSuccessMessage],
    {navigate: staticExtra(navigate), t: staticExtra(t)},
  )
  const onMergeDuplicateStudentsSuccess = useSafeCallbackExtraDeps<
    () => void,
    {mAllRosterFiles: typeof mAllRosterFiles; schools: typeof schools; t: typeof t}
  >(
    ({mAllRosterFiles, schools, t}) =>
      () => {
        ensureAllRosterFiles(mAllRosterFiles, files => {
          setMergeDuplicatesOperationLocation(null)
          setLoadingText(validatedRosterText)
          RosterApi.previewRoster(schoolAdmin.id, files)
            .then((results: Array<SyncResultT>) => {
              const failedItems = failedItemsFromSync(results)
              setLoadingText(null)
              if (failedItems !== null) {
                setPhase({
                  tag: 'validation-errors',
                  value: failedItems,
                })
              } else {
                setPhase({tag: 'confirmation', value: results})
              }
            })
            .catch(err => {
              logError(new HttpError(err))
              setPhase(parseErrorsAndGetState(err, schools, sisIdMatchingAttempted, t))
              setLoadingText(null)
            })
        })
      },
    [validatedRosterText, schoolAdmin.id, sisIdMatchingAttempted],
    {
      mAllRosterFiles: equalExtra(mAllRosterFiles),
      schools: equalExtra(schools),
      t: staticExtra(t),
    },
  )

  const startDryRun = unCallbackFn(onMergeDuplicateStudentsSuccess)

  const onAttach = (file: File, fileType: RosterFileTypeT) => {
    const newFile: RosterFileT = {
      tag: fileType,
      file,
      errorStatus: file.size > MAX_ATTACHMENT_FILE_SIZE ? 'file-too-large' : 'no-error',
    }

    setFiles([...files.filter(file => file.tag !== fileType), newFile])
  }

  const onRemoveAttachment = (fileType: RosterFileTypeT) =>
    setFiles(files.filter(f => f.tag !== fileType))

  const onFixCsv = () => {
    setSisIdMatchingAttempted(false)
    setPhase({tag: 'attachment'})
  }

  const onCancel = () => navigate(Routes.dashboard())

  const exitWithoutSyncing = () => setModal('early-exit')

  const onHideModals = () => setModal(null)

  const onStartOver = () => {
    setFiles([])
    setSisIdMatchingAttempted(false)
    setPhase({tag: 'attachment'})
  }

  const launchRosterSyncOperation = () => {
    ensureAllRosterFiles(mAllRosterFiles, files => {
      setLoadingText(t('IMPORT_ROSTER_SYNCING'))
      RosterApi.uploadRoster(files)
        .then(location => {
          operationPersistence.track(PERSISTENCE_KEY.UPLOAD_ROSTER, location)
          setUploadRosterOperationLocation(location)
        })
        .catch(err => {
          logError(new HttpError(err))
          setPhase({
            tag: 'validation-errors',
            value: onlyUnexpectedError(t),
          })
        })
        .finally(() => setLoadingText(null))
    })
  }

  const onMergeDuplicatesAndStartImport = () => {
    if (isDedupMergeChecked) {
      setModal('confirm-dedup-merge')
    } else {
      startDryRun()
    }
  }

  const onMergeConfirmed = () => {
    ensureAllRosterFiles(mAllRosterFiles, files => {
      setModal(null)
      RosterApi.mergeDuplicateStudentsForCsvSync(files)
        .then(location => {
          operationPersistence.track(PERSISTENCE_KEY.MERGE_DUPLICATES, location)
          setMergeDuplicatesOperationLocation(location)
        })
        .catch(err => {
          logError(new HttpError(err))

          if (err.status === 404) {
            // FIXME: Currently, the backend doesn't handle the case
            // where the same duplicate student is merged more than once
            // into the same real student. This causes 404s because the
            // duplicate student no longer exists after the first merge
            Notification.displayErrorNotification(
              'Unable to finish merging duplicate students. Please try again later.',
              3,
            )
            setPhase({tag: 'attachment'})
          } else {
            setPhase(parseErrorsAndGetState(err, schools, sisIdMatchingAttempted, t))
          }
        })
    })
  }

  const onCheckboxChange = () => setIsDedupMergeChecked(!isDedupMergeChecked)

  const onSelectSisIdMatch = (
    unrecognizedSisIdsAddressed: boolean,
    matchedSelections: Array<SelectedMatchT>,
  ) => {
    setUnrecognizedSisIdsAddressed(unrecognizedSisIdsAddressed)
    setMatchedSelections(matchedSelections)
  }

  const submitMatchedSisIds = () => {
    if (unrecognizedSisIdsAddressed) {
      const schoolPutPromises = matchedSelections.map(({sisId, school}) =>
        updateSchool(school.id, {
          ...school,
          sisId,
        }),
      )

      setSisIdMatchingAttempted(true)

      Promise.all(schoolPutPromises)
        .then(() => {
          const continueImport = isDedupMergeChecked ? onMergeConfirmed : () => startDryRun()
          continueImport()
        })
        .catch(err => {
          logError(new HttpError(err))
          setPhase(parseErrorsAndGetState(err, schools, true, t))
          setLoadingText(null)
        })
    } else {
      Notification.displayErrorNotification(
        'Unable to submit school updates until all unrecognized SisIDs are matched',
        3,
      )
    }
  }

  const renderBody = (): ReactNode => {
    const {numUnmanagedStudents} = calculateUnmanagedStudents(schools, initialSummary)

    switch (phase.tag) {
      case 'attachment':
        return (
          <RosterBody>
            <GetStarted />
            <br />
            <Container>
              <AttachRosters files={files} onAttach={onAttach} onRemove={onRemoveAttachment} />
              <br />

              <CleanUpRoster
                onStart={onMergeDuplicatesAndStartImport}
                confimButtonEnabled={mAllRosterFiles !== null}
                onCheckboxChange={onCheckboxChange}
                isMergeChecked={isDedupMergeChecked}
                activeIdps={getActiveIdps(schools)}
                numUnmanagedStudents={numUnmanagedStudents}
              />
            </Container>
          </RosterBody>
        )

      case 'sis-id-matching':
        const missingSisIds = phase.value
        const schoolsMissingSisId = nullSisIdSchools(schools)
        return (
          <MatchSisId
            schoolsMissingSisIds={schoolsMissingSisId}
            unrecognizedSisIds={missingSisIds}
            onValidate={submitMatchedSisIds}
            onSelect={onSelectSisIdMatch}
          />
        )

      case 'validation-errors':
        return <FailedImport errors={phase.value} onFixCsv={onFixCsv} />

      case 'confirmation':
        const results = phase.value
        const [_failures, successes] = partitionResults(results)
        const finishedMerging =
          isDedupMergeChecked && mergeDuplicatesOperationLocation === null ? (
            <>
              <FinishedMerging />
              <br />
            </>
          ) : null

        return (
          <RosterBody>
            {finishedMerging}
            <ConfirmRosterSync results={successes} onFinish={launchRosterSyncOperation} />
          </RosterBody>
        )

      default:
        throw new Error(`Unerecognized Roster import phase ${(phase as {tag: never}).tag}`)
    }
  }

  const renderFooter = (): ReactNode => {
    switch (phase.tag) {
      case 'attachment':
        return (
          <ImportRosterFooter
            rightChildren={
              <>
                <Button style="tertiary" onClick={onCancel} dataTest="rostering-cancel">
                  {t('IMPORT_ROSTER_BUTTON_CANCEL')}
                </Button>
                <Button
                  onClick={onMergeDuplicatesAndStartImport}
                  disabled={mAllRosterFiles === null}
                  dataTest="rostering-merge-duplicates-and-start-import"
                >
                  {isDedupMergeChecked
                    ? t('IMPORT_ROSTER_BUTTON_START_WITH_MERGE_DUPLICATES')
                    : t('IMPORT_ROSTER_BUTTON_START')}
                </Button>
              </>
            }
          />
        )

      case 'sis-id-matching':
        return (
          <ImportRosterFooter
            leftChildren={
              <Button style="tertiary" onClick={exitWithoutSyncing}>
                {t('IMPORT_ROSTER_BUTTON_EXIT')}
              </Button>
            }
            rightChildren={
              <>
                <Button style="tertiary" onClick={onStartOver}>
                  {t('IMPORT_ROSTER_BUTTON_CANCEL_AND_START_OVER')}
                </Button>
                <Button
                  onClick={() => submitMatchedSisIds()}
                  dataTest="rostering-sisid-matching-continue"
                  disabled={!unrecognizedSisIdsAddressed}
                >
                  {t('IMPORT_ROSTER_BUTTON_VALIDATE_CSVS')}
                </Button>
              </>
            }
          />
        )

      case 'validation-errors':
        return (
          <ImportRosterFooter
            rightChildren={
              <Button onClick={onFixCsv} dataTest="rostering-fix-csv-and-upload-again">
                {t('IMPORT_ROSTER_BUTTON_FIX_CSVS')}
              </Button>
            }
          />
        )

      case 'confirmation':
        return (
          <ImportRosterFooter
            leftChildren={
              <Button style="tertiary" onClick={exitWithoutSyncing}>
                {t('IMPORT_ROSTER_BUTTON_EXIT')}
              </Button>
            }
            rightChildren={
              <>
                <Button style="tertiary" onClick={onStartOver}>
                  {t('IMPORT_ROSTER_BUTTON_CANCEL_AND_START_OVER')}
                </Button>
                <Button onClick={launchRosterSyncOperation} dataTest="rostering-sync-and-finish">
                  {t('IMPORT_ROSTER_BUTTON_FINISH')}
                </Button>
              </>
            }
          />
        )

      default:
        throw new Error(`Unerecognized Roster import phase ${(phase as {tag: never}).tag}`)
    }
  }

  const breadcrumbsConfig = {
    currentItemName: spanDangerous(t('IMPORT_ROSTER_IMPORT_ROSTER_BREADCRUMB')),
    path: [
      {
        url: Routes.dashboard(),
        name: t('IMPORT_ROSTER_DASHBOARD_BREADCRUMB'),
      },
    ],
    reactRouter: true,
  }

  return (
    <>
      <PageWrapper
        title={t('IMPORT_ROSTER_PAGE_WRAPPER_TITLE')}
        breadcrumbsConfig={breadcrumbsConfig}
        addPageWrapperContainerClass={pageWrapper}
      >
        {renderBody()}

        <PendingOperationWhenLocation
          operationLocation={mergeDuplicatesOperationLocation}
          onSuccess={onMergeDuplicateStudentsSuccess}
          onFailure={onMergeDuplicateStudentsFailure}
          onFinish={onMergeDuplicateStudentsFinish}
          i18nKeys={{
            title: 'MERGE_STUDENTS_PENDING_TITLE',
            heading: 'MERGE_STUDENTS_PENDING_HEADING',
            body: 'MERGE_STUDENTS_PENDING_BODY',
          }}
        />

        <PendingOperationWhenLocation
          operationLocation={uploadRosterOperationLocation}
          onSuccess={onUploadRosterSuccess}
          onFailure={onUploadRosterFailure}
          i18nKeys={{
            title: 'UPLOAD_ROSTER_PENDING_TITLE',
            heading: 'UPLOAD_ROSTER_PENDING_HEADING',
            body: 'UPLOAD_ROSTER_PENDING_BODY',
          }}
        />

        {loadingText !== null ? <DelayedSpinner text={loadingText} /> : null}
      </PageWrapper>
      {renderFooter()}

      <ExitModal
        show={modal === 'early-exit'}
        yesText={t('IMPORT_ROSTER_EXIT_MODAL_BUTTON_YES')}
        onLeave={onCancel}
        onCancel={onHideModals}
      />

      <ConfirmMergeModal
        show={modal === 'confirm-dedup-merge'}
        onConfirm={onMergeConfirmed}
        onCancel={onHideModals}
      />
    </>
  )
}

const parseValidateRosterFile = (
  fileType: RosterFileTypeT,
  files: Array<RosterFileT>,
): File | undefined => files.find(f => f.errorStatus === 'no-error' && f.tag === fileType)?.file

const parseAllValidatedRosterFiles = (files: Array<RosterFileT>): AllRosterFiles | null =>
  mthens(
    [
      parseValidateRosterFile('courses', files),
      parseValidateRosterFile('students', files),
      parseValidateRosterFile('teachers', files),
      parseValidateRosterFile('enrollments', files),
    ],
    ([courses, students, teachers, enrollments]) => ({
      courses,
      students,
      teachers,
      enrollments,
    }),
  ) ?? null

function parseErrorsAndGetState(
  response: XMLHttpRequest,
  schools: Array<SchoolAttrs>,
  sisIdMatchingAttempted: boolean,
  t: TranslateT,
): ImportPhaseT {
  const taggedErrors = parseErrorResponse(response)
  const affirmedErrors = getAffirmedErrors(taggedErrors)
  const [unknownSchoolIdErrors, restAffirmedErrors] = partitionAffirmedErrors(affirmedErrors)
  const unaffirmedErrors = getUnaffirmedErrors(taggedErrors)
  const schoolsWithNullSisIds = nullSisIdSchools(schools)

  if (
    unknownSchoolIdErrors.length > 0 &&
    schoolsWithNullSisIds.length > 0 &&
    !sisIdMatchingAttempted
  ) {
    const unknownSchoolIds = uniqueSchoolIdsFromErrors(unknownSchoolIdErrors)

    return {
      tag: 'sis-id-matching',
      value: unknownSchoolIds,
    }
  } else {
    return {
      tag: 'validation-errors',
      value: ensureSomeErrorIsShown(
        [
          ...failedImportErrors([...unknownSchoolIdErrors, ...restAffirmedErrors]),
          ...failedImportErrorsUnaffirmed(unaffirmedErrors),
        ],
        t,
      ),
    }
  }
}

const nullSisIdSchools = (schools: Array<SchoolAttrs>): Array<SchoolAttrs> =>
  sortBy(
    schools.filter(school => school.sisId === null || school.sisId === undefined),
    s => s.name,
  )

const ensureAllRosterFiles = (
  mAllRosterFiles: null | AllRosterFiles,
  effect: (files: AllRosterFiles) => void,
): void => {
  if (mAllRosterFiles !== null) {
    effect(mAllRosterFiles)
  } else {
    logErrorAsUnhandled(
      new Error(
        'Tried to access rostering files when they have not all been supplied. This should have been disallowed by UI components.',
      ),
    )
  }
}
